import moment from "moment";
import { useEffect, useState } from "react";
import { closeModal, Modal, Select, TextInput } from "risa-oasis-ui_v2";
import {
  MedicationClass,
  MedicationCohort,
  MedicationListItem,
} from "../data-model/medicationListModel";
import {
  CLASS_OPTIONS,
  COHORT_OPTIONS,
  DEFAULT_CLASS,
  DEFAULT_COHORT,
} from "../utils/medicationListConstants";

export const ADD_MEDICATION_MODAL_ID = "add-medication-modal";

interface AddMedicationModalProps {
  hasNameInList: (candidate: string) => boolean;
  onConfirm: (item: MedicationListItem) => Promise<void>;
}

const AddMedicationModal = ({
  hasNameInList,
  onConfirm,
}: AddMedicationModalProps) => {
  const [displayName, setDisplayName] = useState("");
  const [klass, setKlass] = useState<MedicationClass>(DEFAULT_CLASS);
  const [cohort, setCohort] = useState<MedicationCohort>(DEFAULT_COHORT);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
  }, [displayName]);

  const reset = () => {
    setDisplayName("");
    setKlass(DEFAULT_CLASS);
    setCohort(DEFAULT_COHORT);
    setError("");
  };

  const handleSave = async () => {
    const trimmed = displayName.trim();
    if (!trimmed) {
      setError("Display name is required");
      return;
    }
    const lower = trimmed.toLowerCase();
    if (hasNameInList(lower)) {
      setError("A medication with this name already exists");
      return;
    }
    const item: MedicationListItem = {
      name: lower,
      display_name: trimmed,
      class: klass,
      cohort: cohort,
      related_drugs: [],
      icd_instructions: [],
      instructions_count: 0,
      source: "manual",
      environment: "prod",
      last_updated: moment().toDate().toISOString(),
    };
    await onConfirm(item);
    reset();
    closeModal(ADD_MEDICATION_MODAL_ID);
  };

  return (
    <Modal
      dialogId={ADD_MEDICATION_MODAL_ID}
      title="Add Medication"
      saveButtonText="Add"
      cancelText="Cancel"
      autoCloseOnSave={false}
      disableSave={!displayName.trim()}
      onSave={handleSave}
      onCancel={reset}
      onClose={reset}
    >
      <div className="flex flex-col gap-3 py-2">
        <TextInput
          id="add-medication-display-name"
          label="Display Name"
          placeholder="e.g. Humira"
          required
          defaultValue={displayName}
          error={error}
          onChange={(e) => setDisplayName(e.value)}
        />
        <Select
          id="add-medication-class"
          label="Generic / Brand"
          placeholder="Select class"
          options={CLASS_OPTIONS}
          defaultValue={klass}
          value={klass}
          onOptionChange={(opt) => setKlass(opt.value as MedicationClass)}
        />
        <Select
          id="add-medication-cohort"
          label="Cohort"
          placeholder="Select cohort"
          options={COHORT_OPTIONS}
          defaultValue={cohort}
          value={cohort}
          onOptionChange={(opt) => setCohort(opt.value as MedicationCohort)}
        />
      </div>
    </Modal>
  );
};

export default AddMedicationModal;
